import type { TicketIncident } from '../components/ServiceNowTicketModal';
import { FTB_DEMO_NOTIFICATION, lookupSite } from './ftbDecision';

// Key identifying which demo mockup a "channel" step opens once it reaches
// 'done'. 'declared' opens a summary of the incident record itself (trigger,
// classification, initial facts); the rest open that channel's own mockup.
// The five 'ftb-*' keys are scenario-specific — see FTB_STEPS below — and
// never appear alongside the other six; ftb-automation's pipeline is its
// own 5-step sequence, not FANOUT_STEPS plus an extra step.
export type MockupKey =
  | 'declared'
  | 'pagerduty'
  | 'teams'
  | 'servicenow'
  | 'ai-report'
  | 'email'
  | 'ftb-notification'
  | 'ftb-snow-lookup'
  | 'ftb-sharepoint-lookup'
  | 'ftb-requirement'
  | 'ftb-notice';

interface FanoutStep {
  label: string;
  delayMs: number;
  mockupKey?: MockupKey;
  colorKey: MockupKey;
}

export const FANOUT_STEPS: FanoutStep[] = [
  { label: 'Major incident declared', delayMs: 400, mockupKey: 'declared', colorKey: 'declared' },
  { label: 'Paging on-call via PagerDuty', delayMs: 2600, mockupKey: 'pagerduty', colorKey: 'pagerduty' },
  { label: 'Posting to Microsoft Teams', delayMs: 2400, mockupKey: 'teams', colorKey: 'teams' },
  { label: 'Opening ServiceNow ticket', delayMs: 2600, mockupKey: 'servicenow', colorKey: 'servicenow' },
  { label: 'Drafting incident report', delayMs: 3000, mockupKey: 'ai-report', colorKey: 'ai-report' },
  { label: 'Emailing stakeholders', delayMs: 2400, mockupKey: 'email', colorKey: 'email' },
];

// major_incident.ticket_id is a UUID FK to a real ticket row, so it can't
// carry a demo scenario marker — RFZ is this scenario's affected_site, and
// no other fixture in SIMULATED_OUTAGE_SCENARIOS uses it, so it doubles as
// the signal for which pipeline an incident gets.
export function isFtbAutomationIncident(incident: FanoutIncident): boolean {
  return incident.affected_site === FTB_DEMO_NOTIFICATION.siteCode;
}

// ftb-automation's own 5-step pipeline, replacing FANOUT_STEPS entirely
// rather than appending to it — this scenario isn't a PagerDuty/Teams/
// ServiceNow/email fan-out, it's the docx's own process broken out one
// stage per system it touches: the NOC notification pull, the SNOW Location
// table lookup (site essentiality), the SharePoint "Processing Centers and
// Surge Sites" lookup (the catch-all rule), the FTB requirement they
// combine into, then the generated notice.
const FTB_STEPS: FanoutStep[] = [
  { label: 'IC/NOC notification received', delayMs: 700, mockupKey: 'ftb-notification', colorKey: 'ftb-notification' },
  { label: 'Checking SNOW Location table', delayMs: 1800, mockupKey: 'ftb-snow-lookup', colorKey: 'ftb-snow-lookup' },
  { label: 'Checking SharePoint site list', delayMs: 1800, mockupKey: 'ftb-sharepoint-lookup', colorKey: 'ftb-sharepoint-lookup' },
  { label: 'Determining FTB requirement', delayMs: 2000, mockupKey: 'ftb-requirement', colorKey: 'ftb-requirement' },
  { label: 'Drafting FTB notice', delayMs: 2200, mockupKey: 'ftb-notice', colorKey: 'ftb-notice' },
];

export function stepsForIncident(incident: FanoutIncident): FanoutStep[] {
  return isFtbAutomationIncident(incident) ? FTB_STEPS : FANOUT_STEPS;
}

// The demo incident from POST /api/outages/simulate's response (see
// createMajorIncident in atlas/apps/api/src/routes/mim.js). Typed loosely
// (rather than mirroring each mockup's own prop interface exactly) since
// this same object fans out to five differently-shaped mockup props below,
// and every field but `id` is optional/nullable on the wire anyway.
export type FanoutIncident = Record<string, any>;

// Maps the raw fan-out incident to ServiceNowTicketModal's own TicketIncident
// shape, which doesn't line up field-for-field with the other four mockups.
export function toTicketIncident(incident: FanoutIncident): TicketIncident {
  return {
    title: incident.impact_statement || incident.service_name || 'Major Incident',
    createdAt: incident.declared_at,
    isResolved: incident.state === 'resolved',
    configurationItem: incident.service_name,
    location: incident.affected_site,
    workNotes: incident.workaround,
  };
}

// The pipeline page's log window narrates the same pipeline as a running,
// color-coded event log. One entry per step (same order/index — see
// stepLogLinesForIncident()), plus a trigger line that fires before the
// pipeline even starts — the alert or notification that supposedly kicked
// off the whole incident — and a closing line once every step has run. Each
// entry's color matches that channel's own mockup's real brand color
// (TeamsPostMockup's purple, PagerDutyAlertMockup's green, etc.), so the log
// and the mockup a reader clicks into agree with each other. Fed by whatever
// the simulated incident actually carries (service/site), falling back to a
// stand-in hostname so the trigger line always reads like a real alert,
// never an empty label.
export interface LogLine {
  text: string;
  color: string;
}

// One brand color per step (plus 'alert' for the pre-pipeline trigger line
// and 'resolution' for the closing line) — shared by the log window's text
// color, the pipeline stage icon's fill/glow, and the connector fill it
// lights up. Real brand colors where the step has a real product behind it
// (PagerDuty green, Teams purple, ServiceNow blue); 'declared'/'alert' use
// red/amber for urgency; 'ai-report'/'resolution' are chosen for contrast
// against their neighbors, not sampled from anything external.
export const STEP_COLORS: Record<MockupKey | 'alert' | 'resolution', string> = {
  alert: '#F5A524',
  declared: '#EF4444',
  pagerduty: '#06AC38',
  teams: '#8B8CE0',
  servicenow: '#4C9AE0',
  'ai-report': '#A78BFA',
  email: '#38BDF8',
  // Reuses Teams' own purple — the docx's first step literally is a Teams
  // chat notification (the IC/NOC Group Chat), not a distinct product.
  'ftb-notification': '#8B8CE0',
  // Reuses ServiceNow's own blue — this step is a real SNOW table lookup.
  'ftb-snow-lookup': '#4C9AE0',
  // SharePoint's own teal brand color.
  'ftb-sharepoint-lookup': '#038387',
  'ftb-requirement': '#F59E0B',
  'ftb-notice': '#D97706',
  resolution: '#34D399',
};

export function triggerLogLine(incident: FanoutIncident): LogLine {
  if (isFtbAutomationIncident(incident)) {
    return {
      text: `IC/NOC Group Chat: new notification — ${FTB_DEMO_NOTIFICATION.ticket}, site ${FTB_DEMO_NOTIFICATION.siteCode}`,
      color: STEP_COLORS.alert,
    };
  }
  const target = incident.service_name || incident.affected_site || 'dev.projectbuilderdemo.com';
  return { text: `Alertmanager: ${target} — critical anomaly detected, escalating to on-call`, color: STEP_COLORS.alert };
}

export const STEP_LOG_LINES: LogLine[] = [
  { text: 'Major incident declared — severity: critical', color: STEP_COLORS.declared },
  { text: 'PagerDuty: paging on-call via Events API v2', color: STEP_COLORS.pagerduty },
  { text: 'Microsoft Teams: incident card posted to #incident-response', color: STEP_COLORS.teams },
  { text: 'ServiceNow: ticket opened (Major Incident Response)', color: STEP_COLORS.servicenow },
  { text: 'Atlas AI: drafting incident report (impact / workaround / ETA)', color: STEP_COLORS['ai-report'] },
  { text: 'Incident Email: notification sent with PDF report attached', color: STEP_COLORS.email },
];

// Mirrors FTB_STEPS above — one log line per step, same order/index.
const FTB_SITE = lookupSite(FTB_DEMO_NOTIFICATION.siteCode);
const FTB_LOG_LINES: LogLine[] = [
  {
    text: `IC/NOC Group Chat: pulled ticket ${FTB_DEMO_NOTIFICATION.ticket} — site ${FTB_DEMO_NOTIFICATION.siteCode}, ${FTB_DEMO_NOTIFICATION.type} outage`,
    color: STEP_COLORS['ftb-notification'],
  },
  {
    text: `ServiceNow Location table: site ${FTB_DEMO_NOTIFICATION.siteCode} — essential = ${FTB_SITE?.essential ? 'yes' : 'no'}`,
    color: STEP_COLORS['ftb-snow-lookup'],
  },
  {
    text: `SharePoint: Processing Centers and Surge Sites — site ${FTB_DEMO_NOTIFICATION.siteCode} on list = ${FTB_SITE?.onSurgeList ? 'yes' : 'no'}`,
    color: STEP_COLORS['ftb-sharepoint-lookup'],
  },
  {
    text: `FTB Automation: checked site ${FTB_DEMO_NOTIFICATION.siteCode} against FTB requirements`,
    color: STEP_COLORS['ftb-requirement'],
  },
  { text: 'FTB Automation: notice drafted from template', color: STEP_COLORS['ftb-notice'] },
];

// Mirrors stepsForIncident() — the log line set matches whichever step list
// this incident is running.
export function stepLogLinesForIncident(incident: FanoutIncident): LogLine[] {
  return isFtbAutomationIncident(incident) ? FTB_LOG_LINES : STEP_LOG_LINES;
}

export const RESOLUTION_LOG_LINE: LogLine = {
  text: 'All channels notified — incident remains open until manually resolved.',
  color: STEP_COLORS.resolution,
};

const FTB_RESOLUTION_LOG_LINE: LogLine = {
  text: 'FTB notice ready — pending manual distribution.',
  color: STEP_COLORS.resolution,
};

export function resolutionLogLineForIncident(incident: FanoutIncident): LogLine {
  return isFtbAutomationIncident(incident) ? FTB_RESOLUTION_LOG_LINE : RESOLUTION_LOG_LINE;
}

// Mirrors the key/label/description shape of the backend's
// SIMULATED_OUTAGE_SCENARIOS (apps/api/src/simulatedOutages.js) — this list
// only drives the button labels shown here, the backend is the source of
// truth for what each scenario actually creates. Shared by DomainHealthPage's
// Outages-tab picker and OutagePipelinePage's own picker, so the two never
// drift out of sync with each other.
export const SIMULATED_OUTAGE_SCENARIOS = [
  { key: 'payment-gateway', label: 'Payment Gateway Timeout', description: 'Critical · checkout requests timing out' },
  { key: 'network-outage', label: 'Network Outage (DR Site)', description: 'Critical · primary link down at the DR site' },
  { key: 'auth-service', label: 'Auth Service Outage', description: 'Critical · logins rejected for all users' },
  { key: 'database-replication', label: 'Database Replication Lag', description: 'Critical · replica lag exceeding 10 minutes' },
  { key: 'email-delivery', label: 'Email Delivery Outage', description: 'Critical · outbound queues delayed' },
  { key: 'website-outage', label: 'Website Outage', description: 'Critical · dev.projectbuilderdemo.com is down' },
  { key: 'ftb-automation', label: 'FTB Automation', description: 'Critical · Site RFZ WAN circuit down — FTB required (INC4200637)' },
];
